/** @jsx jsx */
import React, { useState, useEffect, useRef } from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@material-ui/core';
import { v4 as generateId } from 'uuid';

import { HTMLInputEvent, PhotoTypes, ICustomTheme } from 'interfaces';
import { getExtension, getTypeFromExtension } from 'utils';
import { MIME_TYPES, RESULT_STATUS } from 'constants/index';
import * as S from './styles';

const {
  cssBorderNone,
  cssGreyCircle,
  cssErrorCircle,
  cssSmall,
  cssValidatedCircle,
} = S;

const Photo = ({
  allowedExtensions = ['png', 'jpg', 'jpeg', 'JPG', 'JPEG', 'PNG'],
  disabled = false,
  fileType = MIME_TYPES.IMAGE,
  isCurrentUser = false,
  id = generateId(),
  maxSize = 100,
  minHeight = 0,
  minWidth = 0,
  setOpenModal,
  setUserPhoto,
  small,
  status,
  photoSrc = null,
  userPhoto,
}: PhotoTypes): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const didMountRef = useRef(false);
  const [isLoading, setLoading] = useState(false);
  const [hasDimensionsError, setHasDimensionsError] = useState(false);
  const [hasSizeError, setHasSizeError] = useState(false);
  const [hasTypeError, setHasTypeError] = useState(false);
  const [filesExist, setFilesExist] = useState(true);

  const mimeTypes = allowedExtensions.map(
    (extension) => `${fileType}/${extension}`,
  );

  useEffect(() => {
    if (!filesExist) {
      // eslint-disable-next-line no-console
      console.error('The selected file is corrupted.');
    }

    if (userPhoto) {
      if (hasDimensionsError) {
        // eslint-disable-next-line no-console
        console.error(
          `The selected file dimensions is invalid :\r\n
          The current file dimension is ${userPhoto.width}x${userPhoto.height}px and accepted types files are ${minWidth}x${minHeight}px`,
        );
      }
      if (hasSizeError) {
        // eslint-disable-next-line no-console
        console.error(
          `The selected file size is invalid :\r\n
            The current file size is ${userPhoto.size} and the max accepted size is ${maxSize}`,
        );
      }
      if (hasTypeError) {
        // eslint-disable-next-line no-console
        console.error(
          `The selected file type is invalid :\r\n
          The current file is ${
            userPhoto.extension
          } and accepted types files are ${allowedExtensions.join(', ')}`,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesExist, hasDimensionsError, hasSizeError, hasTypeError]);

  const octetToMo = (octets: number): number => octets / 1048576;

  useEffect(() => {
    /**
     * didMountRef used to not check errors on
     * componentDidMount but only on componentDidUpdate
     */
    if (didMountRef.current && isLoading) {
      if (userPhoto?.type !== fileType || !mimeTypes.includes(userPhoto.mime)) {
        setHasTypeError(true);
      }
      if (userPhoto && maxSize < octetToMo(userPhoto.size)) {
        setHasSizeError(true);
      }
      if (!filesExist || hasTypeError || hasSizeError) {
        setLoading(false);
      }
    } else {
      didMountRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userPhoto,
    filesExist,
    hasTypeError,
    hasSizeError,
    maxSize,
    fileType,
    mimeTypes,
  ]);

  const updateFile = (event: HTMLInputEvent) => {
    setLoading(true);
    setHasSizeError(false);
    setHasTypeError(false);
    setHasDimensionsError(false);
    setFilesExist(Boolean(event?.target?.files?.length));
    const uploadedFile = event?.target?.files?.[0];
    const uploadedFileValue = event.target.value;

    if (!uploadedFileValue || !filesExist) {
      setLoading(false);
      return;
    }

    const domPreview: any = document.getElementById(`photo-${id}`);
    const uploadedFileExtension = getExtension(uploadedFile?.name);
    const uploadedFileMime = `${getTypeFromExtension(
      uploadedFileExtension,
    )}/${uploadedFileExtension}`;

    setUserPhoto((prevItem: any) => ({
      ...prevItem,
      extension: uploadedFileExtension,
      filename: uploadedFile?.name,
      mime: uploadedFileMime,
      preview: URL.createObjectURL(uploadedFile),
      raw: uploadedFile,
      size: uploadedFile?.size,
      type: uploadedFile?.type.split('/')[0].toLowerCase(),
      value: uploadedFileValue,
    }));

    if (uploadedFileMime !== `${MIME_TYPES.IMAGE}/${uploadedFileExtension}`) {
      setHasTypeError(true);
      setLoading(false);
      return;
    }

    const fileReader = new FileReader();

    fileReader.addEventListener('load', () => {
      const el: any = document.createElement('img');
      const { result } = fileReader;

      el.onload = () => {
        if (el.width < minWidth || el.height < minHeight) {
          setHasDimensionsError(true);
          setUserPhoto((prevItem: any) => ({
            ...prevItem,
            height: el.height,
            width: el.width,
          }));
          setLoading(false);
        } else {
          if (domPreview) {
            domPreview.src = result;
          }
          setOpenModal();
        }
      };
      el.src = result;
    });

    if (uploadedFile) {
      fileReader.readAsDataURL(uploadedFile);
    }

    setLoading(false);
  };

  const getUploadPhotoForm = () => (
    <S.Form name="uploadForm">
      <S.Input
        accept={mimeTypes.join(', ')}
        id="photoInput"
        name="photoInput"
        type="file"
        disabled={disabled}
        onChange={updateFile}
      />
      <label htmlFor="photoInput">
        <S.Button disabled={disabled}>
          <S.CustomPhotoIcon disabled={disabled} />
          <S.PostMyPhoto disabled={disabled}>
            {disabled
              ? t('photo_contest.results.upload_disabled')
              : t('photo_contest.results.undefined_status.post_my_photo')}
          </S.PostMyPhoto>
        </S.Button>
      </label>
    </S.Form>
  );

  const getContent = () => {
    if (hasDimensionsError) {
      return (
        <React.Fragment>
          <S.Photo src={photoSrc} className="overlay">
            {getUploadPhotoForm()}
          </S.Photo>
          <S.ErrorMessage>
            {t('core.upload_dimensions_error', {
              imageHeight: userPhoto?.height,
              imageWidth: userPhoto?.width,
              minHeight,
              minWidth,
            })}
          </S.ErrorMessage>
        </React.Fragment>
      );
    }

    if (hasTypeError) {
      return (
        <React.Fragment>
          <S.Photo src={photoSrc} className="overlay">
            {getUploadPhotoForm()}
          </S.Photo>
          <S.ErrorMessage>
            {t('core.upload_type_error', {
              extension: userPhoto?.extension,
              allowedExtensions: allowedExtensions.join(', '),
            })}
          </S.ErrorMessage>
        </React.Fragment>
      );
    }

    if (isLoading) {
      return <S.StyledCircularProgress size={30} />;
    }

    if (!photoSrc || !status) {
      return getUploadPhotoForm();
    }

    if (photoSrc && status === RESULT_STATUS.WAITING) {
      return (
        <S.Photo src={photoSrc} className="overlay">
          <S.Hourglass />
        </S.Photo>
      );
    }

    if (photoSrc && status === RESULT_STATUS.REFUSED) {
      return (
        <S.Photo src={photoSrc} className="overlay">
          {getUploadPhotoForm()}
        </S.Photo>
      );
    }

    return <S.Photo src={photoSrc} />;
  };

  return (
    <S.CircleOut
      css={[
        small && cssSmall,
        (!isCurrentUser || disabled) && cssGreyCircle(theme),
        status === RESULT_STATUS.REFUSED && cssErrorCircle(theme),
        status === RESULT_STATUS.WAITING && cssBorderNone,
        status === RESULT_STATUS.VALIDATED && cssValidatedCircle(theme),
      ]}
    >
      <S.CircleIn disabled={disabled}>{getContent()}</S.CircleIn>
    </S.CircleOut>
  );
};

export default Photo;
